import {
  KeyboardEvent,
  ReactNode,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import {
  Mention,
  MentionItem,
  MentionsInput,
  MentionsInputStyle,
} from 'react-mentions'
import { Box, Button, Stack, Typography, useTheme } from '@mui/material'
import { useStytchMember } from '@stytch/react/b2b'
import { QueryKey } from '@tanstack/react-query'
import { useCreateDocumentComment } from '@/service-library/hooks/document-comments'
import { useSendNotification } from '@/service-library/hooks/notifications'
import { DocComment } from '@/types/comments'
import {
  applyPlainTextEdit,
  autoTagExactNames,
  getMentionTargets,
  parseCommentBody,
  POSITION_TAG_MARKUP,
  POSITION_TAG_REGEX,
  renderCommentText,
  TagCandidate,
  USER_TAG_MARKUP,
  USER_TAG_REGEX,
} from '@/utils/comment-tags'
import { showErrorSnackbar } from '@/utils/snackbars'
import generateUuid from '@/utils/uuid'
import PositionAvatar from '@/components/avatars/PositionAvatar'
import UserAvatar from '@/components/avatars/UserAvatar'
import useCommentTagOptions from './useCommentTagOptions'

const MAX_LENGTH = 1000
const MAX_BODY_LENGTH = 2000
const MIN_ROWS = 4
const MAX_ROWS = 12
const VERTICAL_PADDING = 8.5
const HORIZONTAL_PADDING = 14

const displayTransform = (_id: string, display: string) => `@${display}`

type CommentComposerProps = {
  documentId: string
  /** Whether to load the people and positions offered by the @ menu. */
  enabled: boolean
  /** Must match the query key CommentsDrawer uses to fetch the list, so the
   * optimistic comment lands in the same cached page. */
  listQueryKey: QueryKey
  /** The count badge's query key, invalidated on post so the badge count stays
   * in sync with the list. */
  countQueryKey?: QueryKey
}

type SuggestionRowProps = {
  avatar: ReactNode
  highlighted: ReactNode
}

function SuggestionRow({ avatar, highlighted }: SuggestionRowProps) {
  return (
    <Stack direction="row" spacing={1.5} alignItems="center">
      {avatar}
      <Typography variant="body2" noWrap>
        {highlighted}
      </Typography>
    </Stack>
  )
}

// react-mentions draws the visible text in a layer behind a transparent
// textarea. Both must use the same font, line height and padding, or the
// cursor drifts away from the text the user sees.
function useMentionsInputStyles() {
  const theme = useTheme()

  return useMemo(() => {
    const { fontFamily, fontSize, fontWeight, letterSpacing, lineHeight } =
      theme.typography.body1
    const textStyle = {
      fontFamily,
      fontSize,
      fontWeight,
      letterSpacing,
      lineHeight,
    }
    const rowHeight = `calc(${lineHeight} * 1em)`
    const padding = `${VERTICAL_PADDING}px ${HORIZONTAL_PADDING}px`

    // react-mentions reads `substring` to style the highlighter's text, but
    // its types don't list it, so this object can't be typed inline.
    const highlighter = {
      ...textStyle,
      padding,
      border: 'none',
      color: theme.palette.text.primary,
      minHeight: `calc(${MIN_ROWS} * ${rowHeight} + ${VERTICAL_PADDING * 2}px)`,
      maxHeight: `calc(${MAX_ROWS} * ${rowHeight} + ${VERTICAL_PADDING * 2}px)`,
      boxSizing: 'border-box' as const,
      overflow: 'auto',
      substring: { visibility: 'visible' as const },
    }

    const mentionsStyle: MentionsInputStyle = {
      width: '100%',
      ...textStyle,
      '&multiLine': {
        control: { color: theme.palette.text.primary },
        highlighter,
        input: {
          ...textStyle,
          padding,
          border: 'none',
          outline: 'none',
          overflow: 'auto',
          color: 'transparent',
          caretColor: theme.palette.text.primary,
        },
      },
      suggestions: {
        zIndex: theme.zIndex.modal + 1,
        backgroundColor: theme.palette.background.paper,
        color: theme.palette.text.primary,
        fontFamily: theme.typography.body2.fontFamily,
        fontSize: theme.typography.body2.fontSize,
        borderRadius: theme.shape.borderRadius,
        boxShadow: theme.shadows[8],
        minWidth: 220,
        marginTop: 4,
        overflow: 'hidden',
        list: { maxHeight: 240, overflowY: 'auto', padding: '4px 0' },
        item: {
          padding: '6px 16px',
          '&focused': { backgroundColor: theme.palette.action.selected },
        },
      },
    }

    return {
      mentionsStyle,
      mentionStyle: { color: theme.palette.primary.main },
    }
  }, [theme])
}

export default function CommentComposer({
  documentId,
  enabled,
  listQueryKey,
  countQueryKey,
}: CommentComposerProps) {
  const [draft, setDraft] = useState('')
  const { member } = useStytchMember()

  const { users, positions } = useCommentTagOptions({ enabled })
  const inputRef = useRef<HTMLTextAreaElement>(null)

  const candidates = useMemo<TagCandidate[]>(
    () => [
      ...users.map((user) => ({ type: 'user' as const, ...user })),
      ...positions.map((position) => ({
        type: 'position' as const,
        ...position,
      })),
    ],
    [users, positions],
  )

  const { sendNotification } = useSendNotification()

  const { createDocumentComment } = useCreateDocumentComment({
    listQueryKey,
    sideEffectQueryKeys: [countQueryKey],
    onSuccess: (_data, comment) => {
      const targets = getMentionTargets(comment.body ?? '', documentId)
      if (targets.length === 0) return
      sendNotification({
        id: generateUuid(),
        code: 'mention',
        title: '',
        body: renderCommentText(comment.body ?? ''),
        link: { kind: 'document', obj_id: documentId },
        targets,
      })
    },
    onError: () => {
      showErrorSnackbar("Couldn't post your comment. Please try again.")
    },
  })

  // Turning typed text like "@Ann" into a tag, or an edit inside a tag, makes
  // react-mentions move the cursor. We save the cursor where the user left it
  // and put it back after the new text is drawn.
  const pendingSelection = useRef<{ start: number; end: number } | null>(null)

  useLayoutEffect(() => {
    const selection = pendingSelection.current
    pendingSelection.current = null
    if (selection) {
      inputRef.current?.setSelectionRange(selection.start, selection.end)
    }
  }, [draft])

  const finalBody = useMemo(
    () => autoTagExactNames(draft.trim(), candidates, { final: true }),
    [draft, candidates],
  )
  const renderedLength = renderCommentText(draft).trim().length
  const bodyTooLong = finalBody.length > MAX_BODY_LENGTH
  const overLimit = bodyTooLong || renderedLength > MAX_LENGTH
  const atLimit = bodyTooLong || renderedLength >= MAX_LENGTH
  const postDisabled = renderedLength === 0 || overLimit

  function handleChange(
    _event: unknown,
    newValue: string,
    newPlainText: string,
    mentions: MentionItem[],
  ) {
    // react-mentions deletes a whole tag when an edit touches it, so
    // newPlainText no longer matches what the user typed. While the user types,
    // the textarea already holds the typed text, so we rebuild the draft from it
    // and the tag turns into plain text. Paste, cut and picking a menu item are
    // handled by react-mentions before the textarea changes, so the textarea
    // still holds the old text and we use newValue as it is.
    const input = inputRef.current
    const previousTagCount = parseCommentBody(draft).filter(
      (segment) => segment.type !== 'text',
    ).length
    const typing =
      !!input &&
      input.value !== renderCommentText(draft) &&
      mentions.length <= previousTagCount
    const editedInsideTag = typing && input.value !== newPlainText
    const value = editedInsideTag
      ? applyPlainTextEdit(draft, input.value)
      : newValue
    const nextDraft = autoTagExactNames(value, candidates, {
      final: false,
      caret: typing ? input.selectionStart : undefined,
    })
    if (
      typing &&
      (editedInsideTag || renderCommentText(nextDraft) !== input.value)
    ) {
      pendingSelection.current = {
        start: input.selectionStart,
        end: input.selectionEnd,
      }
    }
    setDraft(nextDraft)
  }

  function handleSubmit() {
    if (postDisabled || !member) return

    // Client-generated id (repo convention, see src/utils/uuid.ts) lets
    // useCreateDetail optimistically append this exact record to the
    // cached list immediately, and revert it automatically if the POST
    // fails.
    const newComment: DocComment = {
      id: generateUuid(),
      document_id: documentId,
      body: finalBody,
      created_at: new Date().toISOString(),
      created_by_user_id: member.external_id as string,
      deleted_at: null,
    }

    setDraft('')
    createDocumentComment(newComment)
  }

  function handleKeyDown(
    event: KeyboardEvent<HTMLTextAreaElement | HTMLInputElement>,
  ) {
    if (event.key === 'Enter' && (event.ctrlKey || event.metaKey)) {
      event.preventDefault()
      handleSubmit()
    }
  }

  const { mentionsStyle, mentionStyle } = useMentionsInputStyles()

  return (
    <Box
      sx={{
        borderTop: (theme) => `1px solid ${theme.palette.divider}`,
        p: 2,
      }}
    >
      <Box
        sx={{
          border: '1px solid',
          borderColor: 'action.disabled',
          borderRadius: 1,
          '&:hover': { borderColor: 'text.primary' },
          '&:focus-within': {
            borderColor: 'primary.main',
            boxShadow: (theme) => `0 0 0 1px ${theme.palette.primary.main}`,
          },
          '& textarea::placeholder': {
            color: 'text.disabled',
            opacity: 1,
          },
        }}
      >
        <MentionsInput
          value={draft}
          onChange={handleChange}
          inputRef={inputRef}
          onKeyDown={handleKeyDown}
          placeholder="Add a comment…"
          style={mentionsStyle}
          allowSuggestionsAboveCursor
          suggestionsPortalHost={document.body}
          a11ySuggestionsListLabel="People and positions to tag"
        >
          <Mention
            trigger="@"
            data={positions}
            markup={POSITION_TAG_MARKUP}
            regex={POSITION_TAG_REGEX}
            displayTransform={displayTransform}
            appendSpaceOnAdd
            style={mentionStyle}
            renderSuggestion={(_suggestion, _search, highlighted) => (
              <SuggestionRow
                avatar={<PositionAvatar />}
                highlighted={highlighted}
              />
            )}
          />
          <Mention
            trigger="@"
            data={users}
            markup={USER_TAG_MARKUP}
            regex={USER_TAG_REGEX}
            displayTransform={displayTransform}
            appendSpaceOnAdd
            style={mentionStyle}
            renderSuggestion={(suggestion, _search, highlighted) => (
              <SuggestionRow
                avatar={<UserAvatar name={String(suggestion.display ?? '')} />}
                highlighted={highlighted}
              />
            )}
          />
        </MentionsInput>
      </Box>
      <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mt: 1 }}>
        {atLimit && (
          <Typography variant="caption" color="error" sx={{ fontWeight: 500 }}>
            Maximum reached
          </Typography>
        )}
        <Box sx={{ flex: 1 }} />
        <Typography
          variant="caption"
          color={
            overLimit
              ? 'error'
              : renderedLength >= MAX_LENGTH * 0.9
              ? 'warning.main'
              : 'text.secondary'
          }
        >
          {renderedLength.toLocaleString()} / {MAX_LENGTH.toLocaleString()}
        </Typography>
        <Button
          variant="contained"
          size="small"
          onClick={handleSubmit}
          disabled={postDisabled}
        >
          Post
        </Button>
      </Stack>
    </Box>
  )
}
